<template>
  <div :class="$style.container">
    <div :class="$style['filter-container']">
      <div :class="$style['due-date-filter']">
        <Dropdown
          v-if="showDueDateFilters"
          v-model="selectedFilters.dueDate"
          :options="dueDateFilters"
          option-label="label"
          option-value="value"
          name="dueDateName"
          @change="(evt) => updateSelectedFilters(evt, 'dueDate')"
        />
      </div>
      <div>
        <span :class="$style['float-left']">
          <PaymentDropdownMultiSelect
            v-if="monthYearFilters.length"
            ref="monthYearDropdown"
            :options="monthYearFilters"
            :placeholder="$t('account.pages.subPageRepayments.monthYear')"
            data-cy="multiselect-dropdown"
            @on-change="(evt) => updateSelectedFilters(evt, 'monthYears')"
          />
          <PaymentDropdownMultiSelect
            v-if="invoiceIdFilters.length"
            ref="invoiceIdDropdown"
            :options="invoiceIdFilters"
            :placeholder="$t('account.pages.subPageRepayments.id')"
            data-cy="multiselect-dropdown"
            @on-change="(evt) => updateSelectedFilters(evt, 'invoiceIds')"
          />
          <PaymentDropdownMultiSelect
            ref="billTypeDropdown"
            :options="billTypeFilters"
            :placeholder="$t('common.type')"
            data-cy="multiselect-dropdown"
            @on-change="(evt) => updateSelectedFilters(evt, 'billTypes')"
          />
          <PaymentDropdownMultiSelect
            v-if="vendorFilters.length"
            ref="vendorDropdown"
            :options="vendorFilters"
            :placeholder="$t('account.pages.subPagePaymentTransactions.vendor')"
            data-cy="multiselect-dropdown"
            @on-change="(evt) => updateSelectedFilters(evt, 'vendors')"
          />
          <PaymentDropdownMultiSelect
            v-if="showStatusFilters"
            ref="statusDropdown"
            :options="statusFilters"
            :placeholder="$t('account.pages.subPagePaymentTransactions.status')"
            data-cy="multiselect-dropdown"
            @on-change="(evt) => updateSelectedFilters(evt, 'statuses')"
          />
        </span>
        <span :class="$style['float-right']">
          <OnboardingButton
            :class="$style['download-csv-cta']"
            :text="$t('account.pages.subPageRepayments.downloadCsv')"
            @click="generateCsv()"
          />
        </span>
      </div>
    </div>
    <DataTable
      :value="repayments"
      responsive-layout="scroll"
      selection-mode="single"
      :loading="isLoading"
      :paginator="true"
      :rows="20"
      class="table-component__spread-paginator-label"
      :paginator-template="{
        '640px':
          'CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink RowsPerPageDropdown',
        default:
          'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
      }"
      :rows-per-page-options="[10, 20, 50]"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      @rowSelect="onRowSelect"
    >
      <template #loading>
        <icon
          v-if="isLoading"
          name="spinner"
          scale="2"
          data-cy="custom-table-payments-loader"
        />
      </template>
      <template #empty>{{ emptyMsg }} </template>
      <Column
        :field="repaymentsType === 'upcoming' ? 'date' : 'createdAt'"
        :header="$t('common.date')"
        header-style="padding-left: 12px"
        body-style="width: 180px"
      >
        <template #body="slotProps">
          {{
            formattedDate(
              repaymentsType === 'upcoming'
                ? slotProps.data.date
                : slotProps.data.scheduledChargeDate
                  ? slotProps.data.scheduledChargeDate
                  : slotProps.data.createdAt,
            )
          }}
        </template>
      </Column>
      <Column
        field="billId"
        :header="$t('common.id')"
        header-style="padding-left: 12px"
        body-style="width: 120px"
      >
        <template #body="slotProps">
          {{ getShortBillId(slotProps?.data.billId) }}
        </template>
      </Column>
      <Column
        field="billType"
        :header="$t('common.type')"
        header-style="padding-left: 12px"
        body-style="width: 140px"
      >
        <template #body="slotProps">
          {{ billTypePaymentVendors[slotProps?.data.billType] }}
        </template>
      </Column>
      <Column
        field="vendor"
        :header="$t('common.vendor')"
        header-style="padding-left: 12px"
      >
        <template #body="slotProps">
          {{ slotProps?.data?.vendor || '-' }}
        </template>
      </Column>
      <Column
        field="amountCents"
        :header="$t('common.amount')"
        header-style="padding-left: 12px"
        body-style="width: 130px"
      >
        <template #body="slotProps">
          {{
            formatMoney(
              slotProps.data.amountCents,
              this.repaymentsType === 'upcoming'
                ? slotProps.data.currency
                : slotProps.data.currencyCode,
            )
          }}
        </template>
      </Column>
      <Column
        field="status"
        :header="$t('common.status')"
        header-style="padding-left: 12px"
        body-style="width: 130px"
      >
        <template #body="slotProps">
          <Badge
            :text="getFormattedStatusLabel(slotProps?.data.status)"
            :theme="getBillStatusTheme(slotProps?.data.status)"
          />
        </template>
      </Column>
    </DataTable>
    <PaymentBillDetailsActionsModal ref="rowItemDetailsModal" />
  </div>
</template>
<script>
import { format } from 'date-fns';
import { mapGetters } from 'vuex';
import { formatMoney } from '@/utils/currency';
import { BillPaymentMethods, TransactionStatusLabel } from '@/data/payments';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { getBillStatusTheme, getShortBillId } from '@/utils/payments';
import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import { CurrencyCode } from '@clearbanc/data-common-types';
import { BillType, billTypePaymentVendors } from '@/@types/bill.types';

import { Badge } from '@clearbanc/design-components/src/components';
import Dropdown from 'primevue/dropdown';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import OnboardingButton from '@/components/buttons/OnboardingButton.vue';
import PaymentDropdownMultiSelect from '@/pages/payments/components/PaymentDropdownMultiSelect.vue';
import PaymentBillDetailsActionsModal from '@/pages/payments/components/PaymentBillDetailsActionsModal/index.vue';
import { useToast } from '@/composables/useToast';

export default {
  components: {
    Badge,
    Column,
    DataTable,
    Dropdown,
    PaymentDropdownMultiSelect,
    PaymentBillDetailsActionsModal,
    OnboardingButton,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  props: {
    repayments: {
      type: Array,
      required: true,
    },
    repaymentsType: {
      type: String,
      required: true,
    },
    selectedFilters: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      areFiltersEmpty: true,
      billTypePaymentVendors,
    };
  },
  methods: {
    getBillStatusTheme,
    getShortBillId,
    formatMoney,
    async onRowSelect(selectedRowItem) {
      let transaction = selectedRowItem.data;
      const id = transaction?.billId;

      if (id) {
        // GET bill details if selectedRowItem is a transaction
        await this.$store.dispatchApiAction('GET_BILL', {
          id,
        });
        transaction = this.selectedBill;
      }
      this.$refs.rowItemDetailsModal.open(transaction);
      if (transaction.paymentMethod !== BillPaymentMethods.BNPL) {
        analytics.track(SEGMENT_EVENTS.CLICKED_BILL_DETAILS, {
          businessId: this.businessId,
          businessName: this.business.name,
          billId: id,
        });
      }
      if (transaction.paymentMethod === BillPaymentMethods.BNPL) {
        analytics.track(SEGMENT_EVENTS.CLICKED_BNPL_BILL_DETAILS, {
          businessId: this.businessId,
          businessName: this.business.name,
          billId: id,
          uploadType: transaction.isReceipt ? 'receipt' : 'invoice',
        });
      }
    },
    getFormattedStatusLabel(status) {
      return TransactionStatusLabel[status];
    },
    resetOtherFilters() {
      if (this.$refs.monthYearDropdown) {
        this.$refs.monthYearDropdown.selectedOptions = null;
      }
      if (this.$refs.invoiceIdDropdown) {
        this.$refs.invoiceIdDropdown.selectedOptions = null;
      }
      if (this.$refs.vendorDropdown) {
        this.$refs.vendorDropdown.selectedOptions = null;
      }
      if (this.$refs.billTypeDropdown) {
        this.$refs.billTypeDropdown.selectedOptions = null;
      }
      if (this.showStatusFilters && this.$refs.statusDropdown) {
        this.$refs.statusDropdown.selectedOptions = null;
      }
    },
    updateSelectedFilters(evt, key) {
      let updatedFilters = { ...this.selectedFilters, [key]: evt.value };

      if (key === 'dueDate' && evt.value !== 0) {
        this.resetOtherFilters();
        updatedFilters = {
          invoiceIds: [],
          monthYears: [],
          vendors: [],
          billTypes: [],
          [key]: evt.value,
          ...(this.repaymentsType !== 'upcoming' ? { statuses: [] } : {}),
        };
      } else {
        updatedFilters.dueDate = 0;
      }
      this.areFiltersEmpty = Object.entries(updatedFilters).every(
        ([filterKey, v]) => (Array.isArray(v) ? v.length === 0 : v === 0),
      );
      this.$emit('on-selected-filters', updatedFilters);
    },
    async generateCsv() {
      this.toast.info(
        this.$t('account.pages.subPageRepayments.downloadWillBegin'),
        {
          summary: this.$t('account.pages.subPageRepayments.generatingCsv'),
          life: 3000,
        },
      );

      await this.$store.dispatchApiAction('DOWNLOAD_PAYMENTS_CSV', {
        businessId: this.businessId,
        subtype: 'bills',
      });

      if (this.downloadCSVRequest.isSuccess) {
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(this.csvDownload);
        downloadLink.download = this.downloadFilename;
        downloadLink.click();
        URL.revokeObjectURL(downloadLink.href);
      } else {
        this.toast.error(
          this.$t('account.pages.subPageRepayments.noTransactionsToDownload'),
          {
            summary: this.$t(
              'account.pages.subPageRepayments.failedToGenerateCsv',
            ),
            life: 3000,
          },
        );
      }
    },
    formattedDate(date) {
      try {
        const dateEDT = new Date(date);
        return format(new Date(dateEDT), 'MMMM dd, yyyy');
      } catch (_error) {
        return date;
      }
    },
  },
  computed: {
    ...mapGetters(['business', 'businessId', 'csvDownload', 'selectedBill']),
    ...mapRequestStatuses({
      downloadCSVRequest: 'DOWNLOAD_PAYMENTS_CSV',
    }),
    monthYearFilters() {
      return (this.filters.monthYears ?? []).map((value) => ({
        label: new Date(value).toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }),
        value,
      }));
    },
    invoiceIdFilters() {
      return (this.filters.invoiceIds ?? []).map((value) => ({
        label: getShortBillId(value),
        value,
      }));
    },
    billTypeFilters() {
      // Always return static options
      return [
        { label: this.$t('common.invoice'), value: BillType.INVOICE },
        { label: this.$t('common.cashAdvance'), value: BillType.CASH_ADVANCE },
      ];
    },
    vendorFilters() {
      return (this.filters.vendors ?? []).map((value) => ({
        label: value,
        value,
      }));
    },
    showStatusFilters() {
      return this.statusFilters.length && this.repaymentsType === 'history';
    },
    statusFilters() {
      return (this.filters.statuses ?? []).map((value) => ({
        label: TransactionStatusLabel[value],
        value,
      }));
    },
    currencyCodes: () => CurrencyCode,
    showDueDateFilters() {
      return this.dueDateFilters.length && this.repaymentsType === 'upcoming';
    },
    dueDateFilters() {
      return (this.filters.dueDate ?? []).map((value, i) => ({
        label:
          i > 0
            ? `${this.$t('account.pages.subPageRepayments.scheduledInNext', {
                numberOfDays: 30 * i,
              })}: ${this.$filters.currency(
                value,
                this.currencyCodes.USD,
                false,
              )}`
            : `${this.$t(
                'account.pages.subPageRepayments.scheduled',
              )}: ${this.$filters.currency(
                value,
                this.currencyCodes.USD,
                false,
              )}`,
        value: 30 * i,
      }));
    },
    downloadFilename() {
      return `${
        this.repaymentsType
      }-transactions-${new Date().toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      })}.csv`;
    },
    emptyMsg() {
      return this.areFiltersEmpty
        ? ''
        : this.$t('account.pages.subPageRepayments.tryClearingFilters');
    },
  },
};
</script>
<style lang="less" module>
.hidden {
  display: none;
}

.filter-container {
  margin: 24px 0;
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  @media only screen and (max-width: 950px) {
    flex-wrap: wrap;
  }

  .due-date-filter {
    display: inline-flex;
    align-items: center;
    margin-bottom: 12px;
    @media only screen and (max-width: 500px) {
      flex: 50%;
      max-width: 9em;
      justify-content: space-between;
    }
  }

  .float-left {
    display: inline-flex;
    align-items: center;
    width: 80%;

    @media only screen and (max-width: 500px) {
      display: flex;
      flex-wrap: wrap-reverse;
    }

    @media only screen and (max-width: 950px) {
      width: 100%;
      justify-content: flex-start;
      flex-wrap: wrap;
    }

    > * {
      margin-right: 12px;
      margin-bottom: 12px;
      width: auto;

      @media only screen and (max-width: 500px) {
        flex: 100%;
        width: 100%;
      }
    }
  }

  .float-right {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    width: 20%;

    @media only screen and (max-width: 950px) {
      justify-content: space-between;
    }

    > * {
      margin: 3px;
      max-width: 12em;

      @media only screen and (max-width: 500px) {
        flex: 50%;
        width: 100%;
        justify-content: space-between;
      }
    }
  }

  .download-csv-cta {
    width: 140px;
    background-color: @color-cc-purple-200;

    &:hover {
      background-color: @color-cc-purple-100 !important;
    }
  }

  @media only screen and (max-width: 500px) {
    position: relative;
    display: flex;
    justify-content: space-between;
    flex-flow: wrap;
    top: unset;
    right: unset;
    margin-top: 20px;
  }
}

.table-container {
  border: 1px solid @color-grey-200;
  border-radius: 4px;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  background-color: @color-white;

  .custom-column {
    font-family: @regular-font;
    font-weight: normal;
  }

  tr[class^='row_'] {
    cursor: pointer;
  }

  .transaction-info {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;

    img {
      margin-left: auto;
      cursor: pointer;

      &:hover {
        background: #aeaeae;
        border-radius: 50%;
      }
    }
  }
}
</style>
